import { useState } from "react";
import { NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import { Activity, Building2, CircleUserRound, ClipboardPlus, Mail, Phone, ShieldAlert, Star, User as UserIcon } from "lucide-react";
import { cn } from "@/lib/cn";
import { useContactDetail } from "@/hooks/useContacts";
import { useAuthStore } from "@/store/useAuthStore";
import { TaskCreateDrawer } from "@/components/task/TaskCreateDrawer";
import { CreateInteractionDrawer } from "@/components/interaction/CreateInteractionDrawer";

const DQ_LABEL: Record<string, string> = {
  unverified: "Не перевірено",
  manager_verified: "Перевірено менеджером",
  client_confirmed: "Підтверджено клієнтом",
  imported: "Імпортовано",
  outdated: "Застаріло",
};

/** Build the nested-tab paths for a contact. */
const tabPath = (id: string, sub?: string) => `/contacts/${id}${sub ? `/${sub}` : ""}`;

/**
 * Phase 3.X-1B sub_2 — Contact DETAIL shell (/contacts/:contactId).
 *
 * Compact envelope (identity + context chip + tab bar) + <Outlet/> for the 6
 * lazy tab panels. Mirrors the A2 Client360Page pattern. Tabs are gated by
 * backend tabAvailability (default-DENY). A contact the user may not read comes
 * back `restricted` → redacted shell ("[Контакт обмежено]", no tabs/methods).
 */
export function ContactDetailPage() {
  const { contactId } = useParams<{ contactId: string }>();
  const navigate = useNavigate();
  const id = contactId ?? "";
  const query = useContactDetail(id);
  // BUNDLE 1 dual-path: OFF (default) = current detail (zero regression);
  // ON = upgraded header (primary/decision-maker badges + safe quick-actions).
  const customView = useAuthStore((s) => s.featureFlags?.customContactView ?? false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [actOpen, setActOpen] = useState(false);

  if (query.isPending) {
    return <ContactDetailSkeleton />;
  }
  if (query.isError || !query.data) {
    return (
      <div className="flex h-full items-center justify-center px-6 py-12">
        <div className="max-w-md space-y-3 text-center">
          <ShieldAlert className="mx-auto h-7 w-7 text-destructive" />
          <h1 className="text-lg font-semibold">Контакт не знайдено або немає доступу</h1>
          <p className="text-sm text-muted-foreground">Перевірте посилання або права доступу.</p>
          <button onClick={() => query.refetch()} className="rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground">
            Повторити
          </button>
        </div>
      </div>
    );
  }

  const env = query.data;
  const idn = env.identity;
  const ta = env.tabAvailability;

  if (env.restricted) {
    return (
      <div className="flex h-full items-center justify-center px-6 py-12">
        <div className="max-w-md space-y-3 text-center">
          <ShieldAlert className="mx-auto h-7 w-7 text-muted-foreground/60" />
          <h1 className="text-lg font-semibold">[Контакт обмежено]</h1>
          <p className="text-sm text-muted-foreground">У вас немає доступу до цього контакту.</p>
          <button onClick={() => navigate("/contacts")} className="rounded-md border px-3.5 py-2 text-sm font-medium hover:bg-accent">
            До списку контактів
          </button>
        </div>
      </div>
    );
  }

  const tabs: Array<{ key: string; label: string; to: string; show: boolean; badge?: number }> = [
    { key: "overview", label: "Огляд", to: tabPath(id), show: ta.identity },
    { key: "methods", label: "Канали зв'язку", to: tabPath(id, "methods"), show: ta.methods, badge: env.methodsSummary.totalVisibleCount },
    { key: "accounts", label: "Контекст", to: tabPath(id, "accounts"), show: ta.accounts },
    { key: "activities", label: "Активності", to: tabPath(id, "activities"), show: ta.activities },
    { key: "contracts", label: "Підписані договори", to: tabPath(id, "contracts"), show: ta.contracts, badge: env.signedContracts.count },
    { key: "merge", label: "Дублікати", to: tabPath(id, "merge-status"), show: ta.mergeStatus, badge: env.mergeStatus.duplicateVisibleCount },
  ];

  const dq = idn.dataQualityStatus ? (DQ_LABEL[idn.dataQualityStatus] ?? idn.dataQualityStatus) : "";

  // Safe-action targets — ONLY from ACL-visible methods (backend returns visible
  // only; null = not present). tel:/mailto built from the visible value.
  const phoneVal = env.methodsSummary.primaryVisible.find((m) => m.channel === "phone")?.value ?? null;
  const emailVal = env.methodsSummary.primaryVisible.find((m) => m.channel === "email")?.value ?? null;
  const qaBtn = "inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs hover:bg-accent";

  return (
    <div className="flex h-full flex-col">
      <header className="border-b px-6 pt-5">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground">
            <CircleUserRound className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-xl font-semibold tracking-tight">{idn.contactDisplayName}</h1>
              {customView && idn.isPrimary && (
                <span className="inline-flex shrink-0 items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary">
                  <Star className="h-3 w-3" /> Основний
                </span>
              )}
              {customView && idn.isDecisionMaker && (
                <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                  Особа, що приймає рішення
                </span>
              )}
              {idn.needsReview && (
                <span className="shrink-0 rounded-full bg-warning px-2 py-0.5 text-[11px] font-medium text-warning-foreground">
                  Перевірити
                </span>
              )}
              {!idn.isActive && (
                <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">неактивний</span>
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              {(idn.roleLabel || idn.position) && <span>{idn.roleLabel || idn.position}</span>}
              {env.context.clientDisplayName && (
                <>
                  {(idn.roleLabel || idn.position) && <span>·</span>}
                  {env.context.clientId ? (
                    <button
                      onClick={() => navigate(`/clients/${env.context.clientId}`)}
                      className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary hover:bg-primary/20"
                      title="Відкрити картку клієнта"
                    >
                      {env.context.clientDisplayName}
                    </button>
                  ) : (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium">{env.context.clientDisplayName}</span>
                  )}
                </>
              )}
              {dq && (<><span>·</span><span className="flex items-center gap-1"><UserIcon className="h-3 w-3" /> {dq}</span></>)}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-3 text-muted-foreground">
            {env.methodsSummary.primaryVisible.some((m) => m.channel === "phone") && <Phone className="h-4 w-4" aria-label="Має телефон" />}
            {env.methodsSummary.primaryVisible.some((m) => m.channel === "email") && <Mail className="h-4 w-4" aria-label="Має пошту" />}
          </div>
        </div>

        {customView && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button onClick={() => setTaskOpen(true)} className={cn(qaBtn, "border-primary/40 bg-primary/5 font-medium text-primary")}>
              <ClipboardPlus className="h-3.5 w-3.5" /> Задача / запит
            </button>
            {env.allowedActions.canViewActivities && (
              <button onClick={() => setActOpen(true)} className={cn(qaBtn, "text-muted-foreground")}>
                <Activity className="h-3.5 w-3.5" /> Активність
              </button>
            )}
            {phoneVal && (
              <a href={`tel:${phoneVal.replace(/\s+/g, "")}`} className={cn(qaBtn, "text-muted-foreground")}>
                <Phone className="h-3.5 w-3.5" /> Дзвінок
              </a>
            )}
            {emailVal && !idn.doNotEmail && (
              <a href={`mailto:${emailVal}`} className={cn(qaBtn, "text-muted-foreground")}>
                <Mail className="h-3.5 w-3.5" /> Email
              </a>
            )}
            {env.context.clientId && (
              <button onClick={() => navigate(`/clients/${env.context.clientId}`)} className={cn(qaBtn, "text-muted-foreground")}>
                <Building2 className="h-3.5 w-3.5" /> Відкрити клієнта
              </button>
            )}
          </div>
        )}

        <nav className="mt-3 flex gap-1 overflow-x-auto" aria-label="Розділи картки контакту">
          {tabs.filter((t) => t.show).map((t) => (
            <NavLink
              key={t.key}
              to={t.to}
              end={t.key === "overview"}
              className={({ isActive }) =>
                cn(
                  "-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors",
                  isActive ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground",
                )
              }
            >
              {t.label}
              {t.badge != null && t.badge > 0 && <span className="ml-1 text-[11px] text-muted-foreground">({t.badge})</span>}
            </NavLink>
          ))}
        </nav>
      </header>

      <div className="flex-1 overflow-y-auto">
        <Outlet context={env} />
      </div>

      {customView && taskOpen && (
        <TaskCreateDrawer open={taskOpen} onClose={() => setTaskOpen(false)} />
      )}
      {customView && actOpen && (
        <CreateInteractionDrawer open={actOpen} onClose={() => setActOpen(false)} entryPoint="global" />
      )}
    </div>
  );
}

function ContactDetailSkeleton() {
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 animate-pulse rounded-xl bg-muted" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-1/3 animate-pulse rounded bg-muted" />
          <div className="h-3 w-1/4 animate-pulse rounded bg-muted" />
        </div>
      </div>
      <div className="h-40 animate-pulse rounded-lg bg-muted" />
    </div>
  );
}
